import { useRef } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import type { StopRef } from "@/lib/dailyRoutes/draft"

type SameAddressDialogProps = {
  // the route being edited: its own stops go without a route name
  route: string
  code: string
  others: StopRef[]
  onChangeAll: () => void
  onOnlyThis: () => void
  // closed without choosing (Escape, click outside)
  onCancel: () => void
}

const SHOWN = 3

const label = (stop: StopRef, route: string): string =>
  stop.route === route ? stop.code : `${stop.code} (${stop.route})`

const describeOthers = (
  t: TFunction,
  others: StopRef[],
  route: string,
): string => {
  const count = others.length
  const shown = others.slice(0, SHOWN).map((stop) => label(stop, route))
  const names = `${shown.join(", ")}${count > SHOWN ? ", …" : ""}`
  return t("maps.sameAddressDialog.description", { count, names })
}

// Mounted only while a choice is pending, so it is always open.
export function SameAddressDialog({
  route,
  code,
  others,
  onChangeAll,
  onOnlyThis,
  onCancel,
}: SameAddressDialogProps) {
  const { t } = useTranslation()
  const changeAllRef = useRef<HTMLButtonElement>(null)
  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <AlertDialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          changeAllRef.current?.focus()
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("maps.sameAddressDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {describeOthers(t, others, route)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onOnlyThis}>
            {t("maps.sameAddressDialog.onlyThis", { code })}
          </AlertDialogCancel>
          <AlertDialogAction ref={changeAllRef} onClick={onChangeAll}>
            {t("maps.sameAddressDialog.changeAll")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
